/**
 * g.segmented - the SEGMENTED CONTROL (a pill track, one raised thumb behind
 * the selected option; Andrei's ask through vux, Sep 2026: the /billing
 * cycle switch read as two grey halves).
 *
 * Markup (radios = the accessible choice; arrow keys move between them):
 *
 *   <div class="segmented" role="group" aria-label="How often you pay">
 *     <input type="radio" class="btn-check" name="cycle" id="cycleMonth" value="month" />
 *     <label for="cycleMonth">Monthly</label>
 *     <input type="radio" class="btn-check" name="cycle" id="cycleYear" value="year" checked />
 *     <label for="cycleYear">Yearly <span class="segmented-pill">Save 20%</span></label>
 *   </div>
 *
 * The CSS alone (genus scss/_segmented.scss) paints the selected label as
 * the raised pill; g.segmented(el) adds the SLIDING thumb: one
 * `.segmented-thumb` behind the options, moved to the selected label on
 * every change (and re-measured on resize / when the control becomes
 * visible - a ResizeObserver). Buttons work too: `.segmented-option`
 * elements with `.active` on the selected one (selekt's switch mode);
 * call g.segmented(el) again after toggling `.active` programmatically.
 * Reduced motion: the thumb jumps (no transition - the scss). g.init
 * enhances every `.segmented` already on the page; controls rendered
 * later call g.segmented(el) themselves.
 *
 * Full reference: docs/libs/genus.md "Segmented control".
 */

function resolve(target) {
	if (!target) return [];
	if (typeof target === 'string') return Array.from(document.querySelectorAll(target));
	if (target instanceof Element) return [target];
	if (typeof target[Symbol.iterator] === 'function') return Array.from(target).filter((t) => t instanceof Element);
	return [];
}

function selectedOption(root) {
	const checked = root.querySelector(':scope > input:checked');
	if (checked) {
		return (checked.id && root.querySelector(`label[for="${CSS.escape(checked.id)}"]`)) || (checked.nextElementSibling?.tagName === 'LABEL' ? checked.nextElementSibling : null);
	}
	return root.querySelector(':scope > .segmented-option.active, :scope > .active');
}

function layout(root) {
	const state = root._segmented;
	if (!state) return;
	const option = selectedOption(root);
	if (!option || !root.offsetWidth || !option.offsetWidth) {
		state.thumb.style.opacity = '0';
		return;
	}
	state.thumb.style.opacity = '1';
	state.thumb.style.width = option.offsetWidth + 'px';
	state.thumb.style.height = option.offsetHeight + 'px';
	state.thumb.style.transform = `translate(${option.offsetLeft}px, ${option.offsetTop}px)`;
}

function enhance(root) {
	if (!root._segmented) {
		const thumb = document.createElement('span');
		thumb.className = 'segmented-thumb';
		thumb.setAttribute('aria-hidden', 'true');
		root.prepend(thumb);
		root.classList.add('segmented-enhanced');
		root._segmented = { thumb };
		root.addEventListener('change', () => layout(root));
		// a button option toggles .active in its own click handler - measure after it
		root.addEventListener('click', () => requestAnimationFrame(() => layout(root)));
		if (typeof ResizeObserver === 'function') {
			const ro = new ResizeObserver(() => layout(root));
			ro.observe(root);
			root._segmented.ro = ro;
		}
	}
	layout(root);
}

export function segmented(target) {
	const roots = resolve(target);
	roots.forEach(enhance);
	return roots.length === 1 ? roots[0] : roots;
}

// The page's own controls: enhanced when genus boots (g.init) and again at
// DOMContentLoaded (a control rendered by a page script that ran later).
export function initSegmented() {
	if (typeof document === 'undefined') return;
	const all = () => segmented(document.querySelectorAll('.segmented'));
	all();
	if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', all, { once: true });
	else window.requestAnimationFrame(all);
}
